import { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { eventApi } from '@/services/api';
import api from '@/services/api';
import { useAuth } from '@/contexts/UserContext';
import SubscriptionCard from '@/components/events/hero/SubscriptionCard';
import type { Event } from '@/types/event';
import { dashboardService } from '@/services/dashboardService';
import './LiveSessionsPage.css';

type LiveSession = {
  id: string;
  title: string;
  category: string;
  dateLabel: string;
  timeLabel: string;
  durationLabel?: string;
  instructor: string;
  instructorTitle?: string;
  instructorSummary?: string;
  instructorRating?: number;
  instructorStudents?: number;
  instructorAvatar?: string;
  coverImage: string;
  tags: string[];
  isLive?: boolean;
  attendees?: number;
  description: string;
  attendanceStatus?: 'attended' | 'missed' | 'registered' | null; // For past sessions
  scheduleAt?: string; // Original ISO date string for accurate date comparison
  zoomDuration?: number; // Duration in minutes for calculating end time
};

// API Response Types
interface ApiEventSchedule {
  id: number;
  fk_event_id: number;
  schedule_at: string;
  description?: string | null;
  zoom_duration?: number;
  thumbnail_url?: string | null;
  banner_url?: string | null;
  host_profile_picture_url?: string | null;
  host_name?: string | null;
  host_experience?: string | null;
  max_seat?: number;
  registrations?: Array<{
    id: number;
    status_code: string;
    user?: {
      id: number;
      first_name: string;
      last_name?: string;
    };
  }>;
  // Accessors might be available even if relationships aren't loaded
  available_seats?: number;
}

interface ApiEvent {
  id: number;
  title: string;
  description: string;
  type: string;
  status_code: string;
  schedules?: ApiEventSchedule[];
  creator?: {
    id: number;
    first_name: string;
    last_name?: string;
    email?: string;
  };
}

interface ApiEventsResponse {
  status: string;
  message: string;
  data: {
    data: ApiEvent[];
    current_page: number;
    per_page: number;
    total: number;
    last_page: number;
  };
}

const convertApiEventToEvent = (apiEvent: ApiEvent): Event => {
  const firstSchedule = apiEvent.schedules?.[0];
  const fallbackDate = firstSchedule?.schedule_at || new Date().toISOString();

  return {
    id: apiEvent.id,
    title: apiEvent.title,
    type: apiEvent.type || 'online talks',
    category: apiEvent.type || 'Online Session',
    description: apiEvent.description || '',
    event_date: firstSchedule?.schedule_at,
    event_end_date: firstSchedule?.schedule_at,
    registrations_count: firstSchedule?.registrations?.length,
    status_code: apiEvent.status_code,
    category_id: 0,
    start_date: fallbackDate,
    end_date: fallbackDate,
    is_online: true,
    price: 0,
    currency: 'MYR',
    featured: false,
    status: 'published',
    max_attendees: firstSchedule?.max_seat,
    next_schedule: firstSchedule
      ? {
          id: firstSchedule.id,
          schedule_at: firstSchedule.schedule_at,
          status_code: apiEvent.status_code,
          formatted_date: firstSchedule.schedule_at,
          thumbnail_url: firstSchedule.thumbnail_url,
          banner_url: firstSchedule.banner_url,
          max_seat: firstSchedule.max_seat,
          zoom_link: undefined,
          zoom_meeting_id: null,
          zoom_meeting_password: null,
          zoom_join_url: null,
          zoom_start_url: null,
          zoom_topic: null,
          zoom_duration: firstSchedule.zoom_duration,
          zoom_settings: undefined,
          is_upcoming: undefined,
          is_past: undefined,
        }
      : undefined,
  };
};

// Cache buster that changes on each page load but stays consistent during the session
// This ensures images load with fresh CORS headers on each page load
let pageLoadCacheBuster: string | null = null;

// Helper function to ensure image URLs load with proper CORS
// Adds a cache-busting parameter if the URL is from S3 to avoid stale cached responses without CORS headers
const ensureImageUrl = (url: string | undefined | null): string => {
  if (!url || url === '/placeholder.svg') {
    return '/placeholder.svg';
  }
  
  // If it's an S3 URL, add a cache-busting parameter to ensure fresh CORS headers
  // This helps avoid cached responses that don't include CORS headers
  if (url.includes('s3.ap-southeast-1.amazonaws.com') || url.includes('s3.amazonaws.com')) {
    // Remove any existing cache-busting parameters first
    const urlWithoutParams = url.split('?')[0];
    const separator = '?';
    // Initialize cache buster once per page load
    if (!pageLoadCacheBuster) {
      pageLoadCacheBuster = Date.now().toString();
    }
    // Use 'v' parameter for better cache-busting compatibility
    return `${urlWithoutParams}${separator}v=${pageLoadCacheBuster}`;
  }
  
  return url;
};

// Helper function to format date and time
const formatDate = (dateString: string): { dateLabel: string; timeLabel: string } => {
  const date = new Date(dateString);
  const dateLabel = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  const timeLabel = date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  return { dateLabel, timeLabel };
};

// Helper function to format duration
const formatDuration = (minutes?: number): string | undefined => {
  if (!minutes) return undefined;
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hours > 0 && mins > 0) {
    return `${hours}hr${hours > 1 ? 's' : ''} ${mins}min${mins > 1 ? 's' : ''}`;
  } else if (hours > 0) {
    return `${hours}hr${hours > 1 ? 's' : ''}`;
  } else {
    return `${mins}min${mins > 1 ? 's' : ''}`;
  }
};

// Transform API event schedules to LiveSession format
const transformScheduleToSession = (event: ApiEvent, schedule: ApiEventSchedule): LiveSession => {
  const scheduleDate = new Date(schedule.schedule_at);
  const now = new Date();
  const isLive = scheduleDate <= now && scheduleDate.getTime() + (schedule.zoom_duration || 60) * 60 * 1000 >= now.getTime();
  const { dateLabel, timeLabel } = formatDate(schedule.schedule_at);
  
  // Calculate attendees count from registrations if available
  // Note: Backend might need to eager load schedules.registrations for this to work
  const attendees = schedule.registrations?.filter(r => r.status_code === 'registered').length;

  // Get instructor info from host_name
  const instructor = schedule.host_name || 'Instructor';

  // Determine category from event type
  const categoryMap: Record<string, string> = {
    'online talks': 'Career Development',
    'prof networking': 'Professional Networking',
  };
  const category = categoryMap[event.type] || event.type;

  // Get cover image - prefer banner, then thumbnail, then placeholder
  // Note: Backend might need to eager load schedules.thumbnail and schedules.banner relationships
  // for thumbnail_url and banner_url accessors to work
  const coverImage = ensureImageUrl(schedule.banner_url || schedule.thumbnail_url || '/placeholder.svg');

  // Get instructor avatar from schedule host profile picture, or use placeholder
  const instructorAvatar = ensureImageUrl(schedule.host_profile_picture_url || '/placeholder.svg');

  // Determine tags based on status
  const tags: string[] = [];
  if (isLive) tags.push('Live Now');
  if (event.status_code === 'active') tags.push('On Series');

  return {
    id: schedule.id.toString(),
    title: event.title,
    category,
    dateLabel,
    timeLabel,
    durationLabel: formatDuration(schedule.zoom_duration),
    instructor,
    instructorTitle: event.creator?.email ? undefined : undefined, // Not available in API
    instructorSummary: schedule.host_experience || undefined,
    instructorRating: undefined, // Not available in API
    instructorStudents: undefined, // Not available in API
    instructorAvatar,
    coverImage,
    tags,
    isLive,
    attendees: attendees !== undefined && attendees > 0 ? attendees : undefined,
    description: schedule.description || event.description || '',
    scheduleAt: schedule.schedule_at, // Store original ISO date for accurate comparison
    zoomDuration: schedule.zoom_duration, // Store duration for calculating end time
  };
};

type TabType = 'my-sessions' | 'upcoming' | 'saved';

const LiveSessionsPage = () => {
  const { isAuthenticated } = useAuth();
  const [selectedSession, setSelectedSession] = useState<LiveSession | null>(null);
  const [sessions, setSessions] = useState<LiveSession[]>([]);
  const [mySessions, setMySessions] = useState<LiveSession[]>([]);
  const [pastSessions, setPastSessions] = useState<LiveSession[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [subscriptionEvent, setSubscriptionEvent] = useState<Event | null>(null);
  const [showSubscriptionDetails, setShowSubscriptionDetails] = useState(false);
  const [userRegistrationsCount, setUserRegistrationsCount] = useState(0);
  const [userSessionQuota, setUserSessionQuota] = useState<number | null>(null);
  const [hasValidSubscription, setHasValidSubscription] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<TabType>('upcoming');
  const [mySessionsCount, setMySessionsCount] = useState(0);
  const [registrationStatusMap, setRegistrationStatusMap] = useState<Map<string, { status: string; scheduleId: string }>>(new Map());
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [isSummaryExpanded, setIsSummaryExpanded] = useState<boolean>(false);
  const [isSummaryLong, setIsSummaryLong] = useState<boolean>(false);
  const summaryRef = useRef<HTMLParagraphElement>(null);
  const pastSessionsRef = useRef<LiveSession[]>([]);
  const defaultTabSetRef = useRef<boolean>(false);
  const registrationCountFetchedRef = useRef<boolean>(false);

  // Set default tab when authenticated and registration count has been fetched
  useEffect(() => {
    if (isAuthenticated && sessions.length > 0 && !defaultTabSetRef.current) {
      // Wait for registration count to be fetched (indicated by registrationCountFetchedRef)
      // Default to 'my-sessions' if user has registrations, otherwise 'upcoming'
      if (registrationCountFetchedRef.current) {
        if (mySessionsCount > 0) {
          setActiveTab('my-sessions');
        } else {
          setActiveTab('upcoming');
        }
        defaultTabSetRef.current = true;
      }
    } else if (!isAuthenticated) {
      // Reset when user logs out
      defaultTabSetRef.current = false;
      registrationCountFetchedRef.current = false;
      setActiveTab('upcoming');
    }
  }, [isAuthenticated, mySessionsCount, sessions.length]);

  // Reset cache buster on component mount to ensure fresh CORS headers on each page load
  useEffect(() => {
    pageLoadCacheBuster = Date.now().toString();
  }, []);

  // Fetch events on component mount
  useEffect(() => {
    const fetchEvents = async () => {
      try {
        setIsLoading(true);
        setError(null);
        
        // Fetch events with type filter for online talks (coaching sessions)
        const response = await eventApi.getEvents({ 
          type: 'online talks',
          status: 'active',
          per_page: 50 // Get more events to have enough schedules
        });

        const data = response.data as ApiEventsResponse;

        if (data.status === 'success' && data.data?.data) {
          // Collect all schedules with their events
          const scheduleEvents: Array<{ event: ApiEvent; schedule: ApiEventSchedule }> = [];
          
          data.data.data.forEach((event) => {
            if (event.schedules && event.schedules.length > 0) {
              event.schedules.forEach((schedule) => {
                // Include all schedules (upcoming, live, and past)
                const scheduleDate = new Date(schedule.schedule_at);
                const now = new Date();
                const isUpcoming = scheduleDate > now;
                const isLive = scheduleDate <= now && scheduleDate.getTime() + (schedule.zoom_duration || 60) * 60 * 1000 >= now.getTime();
                const isPast = scheduleDate.getTime() + (schedule.zoom_duration || 60) * 60 * 1000 < now.getTime();
                
                // Include upcoming and live for main sessions, all for complete list
                if (isUpcoming || isLive) {
                  scheduleEvents.push({ event, schedule });
                }
                
                // Also collect past sessions for history
                if (isPast) {
                  scheduleEvents.push({ event, schedule });
                }
              });
            }
          });

          // Sort by schedule date (upcoming first) before transforming
          scheduleEvents.sort((a, b) => {
            const dateA = new Date(a.schedule.schedule_at);
            const dateB = new Date(b.schedule.schedule_at);
            return dateA.getTime() - dateB.getTime();
          });

          // Transform to LiveSession format
          const transformedSessions: LiveSession[] = scheduleEvents.map(({ event, schedule }) =>
            transformScheduleToSession(event, schedule)
          );

          // Separate upcoming/live from past sessions
          // A session is "past" if it has ended (start time + duration < now)
          const now = new Date();
          const upcomingSessions = transformedSessions.filter(s => {
            if (s.isLive) return true; // Live sessions are always upcoming
            if (!s.scheduleAt) return false; // Can't determine if no schedule date
            
            const sessionStart = new Date(s.scheduleAt);
            const sessionEnd = s.zoomDuration 
              ? new Date(sessionStart.getTime() + s.zoomDuration * 60 * 1000)
              : new Date(sessionStart.getTime() + 60 * 60 * 1000); // Default 1 hour if no duration
            
            return sessionEnd > now; // Session hasn't ended yet
          });
          const pastSessionsList = transformedSessions.filter(s => {
            if (s.isLive) return false; // Live sessions are not past
            if (!s.scheduleAt) return false; // Can't determine if no schedule date
            
            const sessionStart = new Date(s.scheduleAt);
            const sessionEnd = s.zoomDuration 
              ? new Date(sessionStart.getTime() + s.zoomDuration * 60 * 1000)
              : new Date(sessionStart.getTime() + 60 * 60 * 1000); // Default 1 hour if no duration
            
            return sessionEnd < now; // Session has ended
          });

          setSessions(upcomingSessions);
          pastSessionsRef.current = pastSessionsList;
          setPastSessions(pastSessionsList);
          setSubscriptionEvent(scheduleEvents.length > 0 ? convertApiEventToEvent(scheduleEvents[0].event) : null);
        } else {
          setError('Failed to load events');
        }
      } catch (err: any) {
        console.error('Error fetching events:', err);
        // If it's a 401 error and user is not authenticated, don't show error
        // Just show empty sessions list with the login dialog
        if (err.response?.status === 401 && !isAuthenticated) {
          setSessions([]);
          setError(null);
        } else {
          setError(err.response?.data?.message || 'Failed to load events. Please try again later.');
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchEvents();
  }, []);

  // Fetch user's registrations count for Session Allowance (independent of sessions loading)
  useEffect(() => {
    if (!isAuthenticated) {
      setUserRegistrationsCount(0);
      return;
    }

    const fetchRegistrationsCount = async () => {
      try {
        const rawResponse = await api.get('/v1/events/my/registrations');
        const responseData = ((rawResponse?.data as any)?.data as any)?.data ?? (rawResponse?.data as any)?.data ?? rawResponse?.data;
        
        // Count registrations for "online talks" events (career coaching sessions) with status "registered"
        let onlineTalksRegistrationsCount = 0;
        
        (Array.isArray(responseData) ? responseData : []).forEach((reg: any) => {
          if (reg.status_code === 'registered') {
            const eventType = reg.event_schedule?.event?.type || reg.event_schedule?.event_type;
            if (eventType === 'online talks') {
              onlineTalksRegistrationsCount++;
            }
          }
        });

        setUserRegistrationsCount(onlineTalksRegistrationsCount);
      } catch (err) {
        console.error('Error fetching registrations count:', err);
        setUserRegistrationsCount(0);
      }
    };

    fetchRegistrationsCount();
  }, [isAuthenticated]);

  // Fetch user's registered sessions and attendance status
  useEffect(() => {
    if (!isAuthenticated) {
      setMySessions([]);
      setMySessionsCount(0);
      setRegistrationStatusMap(new Map());
      return;
    }

    const fetchMySessions = async () => {
      try {
        // Get raw response to access schedule IDs and status
        const rawResponse = await api.get('/v1/events/my/registrations');
        const responseData = ((rawResponse?.data as any)?.data as any)?.data ?? (rawResponse?.data as any)?.data ?? rawResponse?.data;
        
        // Create a map of schedule IDs to registration status
        const statusMap = new Map<string, { status: string; scheduleId: string }>();
        const registeredScheduleIds = new Set<string>();
        
        (Array.isArray(responseData) ? responseData : []).forEach((reg: any) => {
          const scheduleId = reg.event_schedule?.id?.toString() || reg.fk_event_schedule_id?.toString();
          if (scheduleId) {
            statusMap.set(scheduleId, {
              status: reg.status_code || 'unknown',
              scheduleId: scheduleId
            });
            
            // Only include 'registered' status for active/upcoming sessions
            if (reg.status_code === 'registered') {
              registeredScheduleIds.add(scheduleId);
            }
          }
        });

        setRegistrationStatusMap(statusMap);

        // Filter sessions that user has registered for (upcoming/live)
        const registeredSessions = sessions.filter((session) => {
          return registeredScheduleIds.has(session.id);
        });

        setMySessions(registeredSessions);
        setMySessionsCount(registeredSessions.length);
        registrationCountFetchedRef.current = true;
      } catch (err) {
        console.error('Error fetching my sessions:', err);
        setMySessions([]);
        setMySessionsCount(0);
        setRegistrationStatusMap(new Map());
        registrationCountFetchedRef.current = true; // Mark as fetched even on error
      }
    };

    if (sessions.length > 0) {
      fetchMySessions();
    }
  }, [isAuthenticated, sessions]);

  // Update past sessions with attendance status when registration data is available
  useEffect(() => {
    if (!isAuthenticated) {
      // Clear past sessions if user is not authenticated
      setPastSessions([]);
      return;
    }

    // If no registration data available yet, wait for it
    // But if we've already fetched and got empty results, clear past sessions
    if (pastSessionsRef.current.length === 0) {
      setPastSessions([]);
      return;
    }

    // If registration data is empty, user has no registrations - clear past sessions
    if (registrationStatusMap.size === 0) {
      setPastSessions([]);
      return;
    }

    const basePastSessions = pastSessionsRef.current;
    const updatedPastSessions = basePastSessions.map(session => {
      const registration = registrationStatusMap.get(session.id);
      if (!registration) {
        // User wasn't registered for this session, don't show in history
        return { ...session, attendanceStatus: null };
      }

      // Determine attendance status based on registration status_code
      let attendanceStatus: 'attended' | 'missed' | 'registered' | null = null;
      const status = registration.status.toLowerCase();
      
      if (status === 'attended' || status === 'completed' || status === 'done') {
        attendanceStatus = 'attended';
      } else if (status === 'missed' || status === 'no_show' || status === 'absent') {
        attendanceStatus = 'missed';
      } else if (status === 'registered' || status === 'joined') {
        // User was registered/joined but session passed - consider as missed
        attendanceStatus = 'missed';
      }

      return { ...session, attendanceStatus };
    }).filter(session => session.attendanceStatus !== null); // Only show sessions user was registered for

    setPastSessions(updatedPastSessions);
  }, [registrationStatusMap, isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) {
      setUserRegistrationsCount(0);
      setUserSessionQuota(null);
      setHasValidSubscription(false);
      return;
    }

    let isMounted = true;

    const extractProfileData = (response: any) => {
      if (!response) return null;
      if (response.data?.data) return response.data.data;
      if (response.data) return response.data;
      return response;
    };

    const getNumber = (value: any): number | null => {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : null;
    };

    const fetchUserSessionQuota = async () => {
      try {
        const response = await dashboardService.getUserProfile();
        const profileData = extractProfileData(response);
        
        // Check if user has a valid subscription
        const subscription = profileData?.subscription || profileData?.data?.subscription;
        const hasSubscription = subscription !== null && subscription !== undefined;
        
        if (!isMounted) {
          return;
        }

        setHasValidSubscription(hasSubscription);

        // Look for quotas in events.quotas (based on API response structure)
        const quotas =
          profileData?.events?.quotas ||
          profileData?.data?.events?.quotas ||
          profileData?.quotas ||
          profileData?.data?.quotas ||
          profileData?.user?.quotas ||
          [];

        const careerQuota = Array.isArray(quotas)
          ? quotas.find((quota: any) => quota?.event_type === 'career_coaching')
          : null;

        if (careerQuota) {
          const totalQuota =
            getNumber(careerQuota?.quota) ??
            getNumber(careerQuota?.total_quota);
          
          if (totalQuota !== null) {
            setUserSessionQuota(totalQuota);
          } else {
            setUserSessionQuota(null);
          }
          
          // Note: userRegistrationsCount is now set from actual registrations count
          // in the fetchMySessions useEffect, not calculated from quota
        } else {
          setUserSessionQuota(null);
        }
      } catch (err) {
        console.error('Error fetching profile quotas:', err);
        if (isMounted) {
          setUserRegistrationsCount(0);
          setUserSessionQuota(null);
          setHasValidSubscription(false);
        }
      }
    };

    fetchUserSessionQuota();

    return () => {
      isMounted = false;
    };
  }, [isAuthenticated]);

  // Handle modal keyboard events
  useEffect(() => {
    if (!selectedSession) {
      return;
    }

    const handleKeydown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setSelectedSession(null);
      }
    };

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', handleKeydown);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleKeydown);
    };
  }, [selectedSession]);

  // Check if instructor summary is too long and needs collapsing
  useEffect(() => {
    if (!selectedSession?.instructorSummary) {
      setIsSummaryLong(false);
      setIsSummaryExpanded(false);
      return;
    }

    // Reset expanded state when session changes
    setIsSummaryExpanded(false);

    // Use a timeout to ensure DOM is rendered before checking
    const timeoutId = setTimeout(() => {
      if (!summaryRef.current) return;

      const element = summaryRef.current;
      const lineHeight = parseFloat(window.getComputedStyle(element).lineHeight) || 20;
      const maxHeight = lineHeight * 3; // 3 lines
      
      // Create a temporary element to measure full height without collapsed class
      const tempElement = element.cloneNode(true) as HTMLElement;
      tempElement.style.position = 'absolute';
      tempElement.style.visibility = 'hidden';
      tempElement.style.height = 'auto';
      tempElement.style.maxHeight = 'none';
      tempElement.classList.remove('collapsed');
      document.body.appendChild(tempElement);
      
      const fullHeight = tempElement.scrollHeight;
      document.body.removeChild(tempElement);
      
      if (fullHeight > maxHeight) {
        setIsSummaryLong(true);
      } else {
        setIsSummaryLong(false);
      }
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [selectedSession?.instructorSummary]);

  const handleCloseModal = () => setSelectedSession(null);
  const handleDetailsClick = (session: LiveSession) => setSelectedSession(session);
  const modalTitleId = selectedSession ? `session-modal-title-${selectedSession.id}` : 'session-modal-title';

  // Filter sessions based on search query
  const filterSessions = (sessionsToFilter: LiveSession[]): LiveSession[] => {
    if (!searchQuery.trim()) {
      return sessionsToFilter;
    }

    const query = searchQuery.toLowerCase().trim();
    return sessionsToFilter.filter((session) => {
      const titleMatch = session.title.toLowerCase().includes(query);
      const descriptionMatch = session.description.toLowerCase().includes(query);
      const instructorMatch = session.instructor.toLowerCase().includes(query);
      const categoryMatch = session.category.toLowerCase().includes(query);
      const tagsMatch = session.tags.some(tag => tag.toLowerCase().includes(query));

      return titleMatch || descriptionMatch || instructorMatch || categoryMatch || tagsMatch;
    });
  };

  return (
    <div className="sessions-page">
      <div className="sessions-header">
        <div className="sessions-header-top">
          <div className="sessions-header-left">
            <h1 className="text-white text-2xl md:text-4xl font-bold tracking-wide">Online Coaching Sessions</h1>
            <p className="text-white/90 text-sm md:text-base font-normal leading-5 sm:leading-7">Join expert-led sessions and level up your skills in real-time</p>
          </div>
          {/* Login Required Dialog */}
          {!isAuthenticated && (
            <div className="flex-col sm:top-1 sm:right-6 login-required-dialog -mx-4 sm:mx-0">
              <div className="login-dialog-content">
                <div className="w-full sm:max-w-fit flex items-center justify-center sm:justify-start">
                  <img
                    src="/inform.svg"
                    alt="Information"
                    className="login-dialog-icon"
                    width="20"
                    height="20"
                  />
                </div>
                <div className="login-dialog-text text-left">
                  <div className="login-dialog-title">Please sign up or log in to register</div>
                  <div className="login-dialog-subtitle">You need an account to register for coaching sessions.</div>
                </div>
                <Link to="/signup" className="login-dialog-signup-btn">
                  Sign Up
                </Link>
              </div>
            </div>
          )}
        </div>
        <div className="sessions-header-search">
          <div className="search-bar">
            <input 
              type="text"
              placeholder="Search sessions, topics, or instructors..." 
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>
        {isAuthenticated && subscriptionEvent && (
          <div className="sessions-subscription-card">
            <SubscriptionCard
              event={subscriptionEvent}
              showDetails={showSubscriptionDetails}
              onToggleDetails={() => setShowSubscriptionDetails((prev) => !prev)}
              variant="liveSessions"
              userRegistrationsCount={userRegistrationsCount}
              userSessionQuota={userSessionQuota ?? undefined}
              hasValidSubscription={hasValidSubscription}
            />
          </div>
        )}
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="sessions-loading">
          <p>Loading sessions...</p>
        </div>
      )}

      {/* Error State */}
      {error && !isLoading && (
        <div className="sessions-error">
          <p>{error}</p>
          <button onClick={() => window.location.reload()}>Retry</button>
        </div>
      )}

      {/* Featured */}
      {!isLoading && !error && filterSessions(sessions).length > 0 && (
        <div className="featured-section">
          {filterSessions(sessions).slice(0, 1).map((s) => {
            const featuredLink = s.isLive 
              ? `/online-coaching/join/${s.id}`
              : `/online-coaching/register/${s.id}`;
            
            return (
              <Link 
                to={featuredLink} 
                className="featured-card" 
                key={s.id}
                onClick={(e) => {
                  // Prevent navigation when clicking on the join/register button
                  const target = e.target as HTMLElement;
                  if (target.closest('.join-btn')) {
                    e.preventDefault();
                  }
                }}
              >
                <img 
                  src={s.coverImage} 
                  alt={s.title} 
                  className="featured-media" 
                  crossOrigin="anonymous"
                  referrerPolicy="no-referrer"
                  loading="lazy"
                />
                <div className="featured-overlay-gradient" />
                <div className="featured-content">
                  <div className="featured-top">
                    {s.isLive && (
                      <div className="badge live">
                        <svg
                          className="icon"
                          width="9"
                          height="9"
                          viewBox="0 0 9 9"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <circle cx="4.5" cy="4.5" r="4" fill="#FFFFFF" />
                        </svg>
                        LIVE NOW
                      </div>
                    )}
                    {typeof s.attendees === 'number' && (
                      <div className="badge attendees">
                        <svg
                          className="icon"
                          width="14"
                          height="14"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <circle cx="9" cy="7" r="4" stroke="currentColor" strokeWidth="2" />
                          <path
                            d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        {s.attendees.toLocaleString()}
                      </div>
                    )}
                  </div>
                  <div className="featured-badges">
                    <span className="badge purple">{s.category}</span>
                    <span className="badge date-badge">
                      <svg
                        className="icon"
                        width="12"
                        height="12"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <rect x="3" y="5" width="18" height="16" rx="2" stroke="currentColor" strokeWidth="2" />
                        <path d="M3 10h18" stroke="currentColor" strokeWidth="2" />
                        <path d="M8 3v4M16 3v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                      </svg>
                      {s.dateLabel} • {s.timeLabel}
                    </span>
                    {s.durationLabel && (
                      <span className="badge duration-badge">
                        <svg
                          className="icon"
                          width="13"
                          height="13"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path d="M12 8v5l4 2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" />
                        </svg>
                        {s.durationLabel}
                      </span>
                    )}
                  </div>
                  <h2 className="featured-title">{s.title}</h2>
                  <div className="featured-bottom">
                    <div className="featured-instructor-section">
                      <div className="featured-instructor">
                        <img src={s.instructorAvatar || '/placeholder.svg'} alt={s.instructor} />
                        <div className="instructor-info">
                          <div className="instructor-name">{s.instructor}</div>
                          <div className="instructor-label">Instructor</div>
                        </div>
                      </div>
                      {/* <button className="action-btn share-btn">
                        <svg
                          width="18"
                          height="18"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <circle cx="18" cy="5" r="3" stroke="currentColor" strokeWidth="2" />
                          <circle cx="6" cy="12" r="3" stroke="currentColor" strokeWidth="2" />
                          <circle cx="18" cy="19" r="3" stroke="currentColor" strokeWidth="2" />
                          <path d="m8.59 13.51 6.83 3.98M15.41 6.51l-6.82 3.98" stroke="currentColor" strokeWidth="2" />
                        </svg>
                      </button> */}
                    </div>
                    {s.isLive ? (
                      <div className="join-btn" onClick={(e) => e.stopPropagation()}>
                        <svg
                          className="icon"
                          width="18"
                          height="18"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        Join Now
                      </div>
                    ) : (
                      <div className="join-btn" onClick={(e) => e.stopPropagation()}>
                        {registrationStatusMap.has(s.id) && registrationStatusMap.get(s.id)?.status === 'registered' 
                          ? 'View Session Details' 
                          : 'Register'}
                      </div>
                    )}
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      )}

      {/* Grid */}
      {!isLoading && !error && (
        <>
          <div className="sessions-toolbar">
            {isAuthenticated && (
              <button 
                className={`tab ${activeTab === 'my-sessions' ? 'active my-sessions-tab' : ''}`}
                onClick={() => setActiveTab('my-sessions')}
              >
                <span>My Sessions</span>
                {mySessionsCount > 0 && (
                  <span className="tab-badge">{mySessionsCount}</span>
                )}
              </button>
            )}
            <button 
              className={`tab ${activeTab === 'upcoming' ? 'active upcoming-tab' : ''}`}
              onClick={() => setActiveTab('upcoming')}
            >
              <svg
                className="tab-icon"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <rect x="3" y="5" width="18" height="16" rx="2" stroke="currentColor" strokeWidth="2" />
                <path d="M3 10h18" stroke="currentColor" strokeWidth="2" />
                <path d="M8 3v4M16 3v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
              <span>Upcoming</span>
            </button>
            {/* <button 
              className={`tab ${activeTab === 'saved' ? 'active saved-tab' : ''}`}
              onClick={() => setActiveTab('saved')}
            >
              <svg
                className="tab-icon"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M19 21V5a2 2 0 0 0-2-2H7a2 2 0 0 0-2 2v16l7-4 7 4Z"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <span>Saved</span>
            </button> */}
          </div>

          {activeTab === 'my-sessions' ? (
            <div className="my-sessions-container">
              <div className="learning-journey-header">
                <h2 className="learning-journey-title">Your Learning Journey</h2>
                <p className="learning-journey-subtitle">Track your registered and completed sessions.</p>
              </div>
              <div className="learning-journey-content">
                <div className="registered-sessions-container">
                  <div className="container-header">
                    <h3 className="container-title">Registered Sessions</h3>
                    <p className="container-subtitle">
                      {searchQuery.trim() 
                        ? `${filterSessions(mySessions).length} of ${mySessions.length} active registration${mySessions.length !== 1 ? 's' : ''}`
                        : `${mySessions.length} active registration${mySessions.length !== 1 ? 's' : ''}`
                      }
                    </p>
                  </div>
                  <div className="registered-sessions-list">
                    {filterSessions(mySessions).length === 0 ? (
                      <div className="empty-state">
                        <p>{searchQuery.trim() ? 'No registered sessions match your search.' : 'No registered sessions at the moment.'}</p>
                      </div>
                    ) : (
                      filterSessions(mySessions).map((s) => {
                        const sessionLink = s.isLive 
                          ? `/online-coaching/join/${s.id}`
                          : `/online-coaching/register/${s.id}`;
                        
                        return (
                          <Link
                            to={sessionLink}
                            className="registered-session-item"
                            key={s.id}
                            onClick={(e) => {
                              // Prevent navigation when clicking on the button
                              const target = e.target as HTMLElement;
                              if (target.closest('.join-live-btn')) {
                                e.preventDefault();
                              }
                            }}
                          >
                            <div className="registered-content">
                              <div className="registered-thumbnail">
                                <img 
                                  src={s.coverImage} 
                                  alt={s.title}
                                  crossOrigin="anonymous"
                                  referrerPolicy="no-referrer"
                                  loading="lazy"
                                />
                                {s.isLive && (
                                  <div className="live-badge-overlay">LIVE</div>
                                )}
                              </div>
                              <div className="registered-details">
                                <h4 className="registered-title">{s.title}</h4>
                                <div className="registered-meta">
                                  <span className="registered-category">{s.category}</span>
                                  <span className="registered-date">
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                      <rect x="3" y="5" width="18" height="16" rx="2" stroke="currentColor" strokeWidth="2" />
                                      <path d="M3 10h18" stroke="currentColor" strokeWidth="2" />
                                      <path d="M8 3v4M16 3v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                                    </svg>
                                    {s.dateLabel} {s.timeLabel}
                                  </span>
                                </div>
                              </div>
                            </div>
                            <div className="registered-action">
                              {s.isLive ? (
                                <div
                                  className="join-live-btn"
                                  onClick={(e) => {
                                    // Match the non-navigation behavior of "Join Now"
                                    e.stopPropagation();
                                  }}
                                >
                                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
                                    <path d="M10 8l6 4-6 4V8z" fill="currentColor" />
                                  </svg>
                                  Join Live Session
                                </div>
                              ) : (
                                <div className="view-session-btn" onClick={(e) => e.stopPropagation()}>
                                  View Details
                                </div>
                              )}
                            </div>
                          </Link>
                        );
                      })
                    )}
                  </div>
                </div>
                <div className="recent-history-container">
                  <div className="container-header">
                    <div className="container-title-with-icon">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
                        <path d="M12 6v6l4 2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                      </svg>
                      <h3 className="container-title">Recent History</h3>
                    </div>
                    <p className="container-subtitle">
                      {pastSessions.filter(s => s.attendanceStatus === 'attended').length} attended, {pastSessions.filter(s => s.attendanceStatus === 'missed').length} missed
                    </p>
                  </div>
                  <div className="history-sessions-list">
                    {pastSessions.length === 0 ? (
                      <div className="empty-state">
                        <p>No session history available.</p>
                      </div>
                    ) : (
                      pastSessions.slice(0, 5).map((s) => (
                        <div className="history-session-item" key={s.id}>
                          <div className="history-thumbnail">
                            <img 
                              src={s.coverImage} 
                              alt={s.title}
                              crossOrigin="anonymous"
                              referrerPolicy="no-referrer"
                              loading="lazy"
                            />
                          </div>
                          <div className="history-details">
                            <h4 className="history-title">{s.title}</h4>
                            <div className="history-meta">
                              <span className="history-date">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <rect x="3" y="5" width="18" height="16" rx="2" stroke="currentColor" strokeWidth="2" />
                                  <path d="M3 10h18" stroke="currentColor" strokeWidth="2" />
                                  <path d="M8 3v4M16 3v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                                </svg>
                                {s.dateLabel} • {s.instructor}
                              </span>
                            </div>
                          </div>
                          <div className="history-status">
                            {s.attendanceStatus === 'attended' ? (
                              <span className="status-badge done">
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M20 6L9 17l-5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                                Done
                              </span>
                            ) : s.attendanceStatus === 'missed' ? (
                              <span className="status-badge missed">Missed</span>
                            ) : null}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            (() => {
              const displaySessions = activeTab === 'upcoming' ? sessions : [];
              const filteredSessions = filterSessions(displaySessions);
              const emptyMessage = activeTab === 'upcoming'
                ? (searchQuery.trim() ? 'No upcoming sessions match your search.' : 'No upcoming sessions available at the moment.')
                : 'No saved sessions.';

              return filteredSessions.length === 0 ? (
                <div className="sessions-empty">
                  <p>{emptyMessage}</p>
                </div>
              ) : (
                <div className="sessions-grid">
                  {filteredSessions.map((s) => {
                    const cardLink = s.isLive 
                      ? `/online-coaching/join/${s.id}`
                      : `/online-coaching/register/${s.id}`;
                    
                    return (
                      <Link 
                        to={cardLink} 
                        className="session-card" 
                        key={s.id}
                        onClick={(e) => {
                          // Prevent navigation when clicking on Details button or action buttons
                          const target = e.target as HTMLElement;
                          if (target.closest('.btn.ghost') || target.closest('.btn.primary')) {
                            e.preventDefault();
                          }
                        }}
                      >
                        <div className="media">
                          <img 
                            src={s.coverImage} 
                            alt="" 
                            crossOrigin="anonymous"
                            referrerPolicy="no-referrer"
                            loading="lazy"
                          />
                          <div className="media-overlay" />
                          <div className="badges">
                            <span className="badge purple">{s.category}</span>
                            <span className="badge date-badge">
                              <svg
                                className="icon"
                                width="12"
                                height="12"
                                viewBox="0 0 24 24"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <rect x="3" y="5" width="18" height="16" rx="2" stroke="currentColor" strokeWidth="2" />
                                <path d="M3 10h18" stroke="currentColor" strokeWidth="2" />
                                <path d="M8 3v4M16 3v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                              </svg>
                              {s.dateLabel} • {s.timeLabel}
                            </span>
                          </div>
                        </div>
                        <div className="content">
                          <h3 className="title title-clamp">{s.title}</h3>
                          <div className="instructor">
                            <img src={s.instructorAvatar || '/placeholder.svg'} alt="" />
                            <span>{s.instructor}</span>
                          </div>
                          <p className="desc">{s.description}</p>
                          <div className="footer">
                            <div className="actions">
                              {s.isLive ? (
                                <div className="btn primary" onClick={(e) => e.stopPropagation()}>
                                  <svg
                                    className="icon"
                                    width="18"
                                    height="18"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                    style={{ marginRight: '8px' }}
                                  >
                                    <path
                                      d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"
                                      stroke="currentColor"
                                      strokeWidth="2"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    />
                                    <path
                                      d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"
                                      stroke="currentColor"
                                      strokeWidth="2"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    />
                                  </svg>
                                  Join Now
                                </div>
                              ) : (
                                <div className="btn primary" onClick={(e) => e.stopPropagation()}>
                                  {registrationStatusMap.has(s.id) && registrationStatusMap.get(s.id)?.status === 'registered' 
                                    ? 'View Session Details' 
                                    : 'Register'}
                                </div>
                              )}
                              <button
                                type="button"
                                className="btn ghost"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  handleDetailsClick(s);
                                }}
                              >
                                Details
                              </button>
                            </div>
                          </div>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              );
            })())}
          </>
        )}

      {selectedSession && (
        <div className="session-modal" role="dialog" aria-modal="true" aria-labelledby={modalTitleId}>
          <div className="modal-backdrop" onClick={handleCloseModal} />
          <div className="modal-panel">
            <button
              type="button"
              className="modal-close"
              onClick={handleCloseModal}
              aria-label="Close session details"
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                <path d="M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </button>
            <div className="modal-media">
              <img 
                src={selectedSession.coverImage} 
                alt={selectedSession.title} 
                crossOrigin="anonymous"
                referrerPolicy="no-referrer"
              />
              <div className="modal-media-gradient" />
              <div className="modal-media-bottom">
                <div className="modal-media-chips">
                {selectedSession.durationLabel && (
                  <span className="modal-chip duration">
                    <svg
                      className="icon"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path d="M12 8v5l4 2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" />
                    </svg>
                    {selectedSession.durationLabel}
                  </span>
                )}
                <span className="modal-chip date">
                  <svg
                    className="icon"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <rect x="3" y="5" width="18" height="16" rx="2" stroke="currentColor" strokeWidth="2" />
                    <path d="M3 10h18" stroke="currentColor" strokeWidth="2" />
                    <path d="M8 3v4M16 3v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  </svg>
                  {selectedSession.dateLabel} • {selectedSession.timeLabel}
                </span>
                {selectedSession.isLive && <span className="modal-chip live">Live Now</span>}
                </div>
                {/* <div className="modal-media-actions">
                  <button type="button" className="modal-circle-btn" aria-label="Save session">
                    <svg
                      width="22"
                      height="22"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M19 21V5a2 2 0 0 0-2-2H7a2 2 0 0 0-2 2v16l7-4 7 4Z"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                  <button type="button" className="modal-circle-btn" aria-label="Share session">
                    <svg
                      width="22"
                      height="22"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <circle cx="18" cy="5" r="3" stroke="currentColor" strokeWidth="2" />
                      <circle cx="6" cy="12" r="3" stroke="currentColor" strokeWidth="2" />
                      <circle cx="18" cy="19" r="3" stroke="currentColor" strokeWidth="2" />
                      <path d="m8.59 13.51 6.83 3.98M15.41 6.51l-6.82 3.98" stroke="currentColor" strokeWidth="2" />
                    </svg>
                  </button>
                </div> */}
              </div>
            </div>
            <div className="modal-body">
              <h3 className="modal-title" id={modalTitleId}>
                {selectedSession.title}
              </h3>
              <span className="modal-category">{selectedSession.category}</span>
              <div className="modal-instructor-card">
                <img src={selectedSession.instructorAvatar || '/placeholder.svg'} alt={selectedSession.instructor} />
                <div className="modal-instructor-content">
                  <div className="modal-instructor-name text-left">{selectedSession.instructor}</div>
                  {selectedSession.instructorTitle && (
                    <div className="modal-instructor-title">{selectedSession.instructorTitle}</div>
                  )}
                  {selectedSession.instructorSummary && (
                    <div className="modal-instructor-summary-wrapper">
                      <p 
                        ref={summaryRef}
                        className={`modal-instructor-summary ${!isSummaryExpanded && isSummaryLong ? 'collapsed' : ''}`}
                      >
                        {selectedSession.instructorSummary}
                      </p>
                      {isSummaryLong && (
                        <button
                          type="button"
                          className="modal-summary-toggle"
                          onClick={() => setIsSummaryExpanded(!isSummaryExpanded)}
                        >
                          {isSummaryExpanded ? 'Show less' : 'Read more'}
                        </button>
                      )}
                    </div>
                  )}
                  <div className="modal-metrics">
                    {typeof selectedSession.instructorRating === 'number' && (
                      <div className="modal-metric">
                        <img src="/star.svg" alt="Star rating" className="icon star" width="16" height="16" />
                        {selectedSession.instructorRating.toFixed(1)}
                      </div>
                    )}
                    {typeof selectedSession.instructorStudents === 'number' && (
                      <div className="modal-metric">
                        <svg
                          className="icon"
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <circle cx="9" cy="7" r="4" stroke="currentColor" strokeWidth="2" />
                        </svg>
                        {selectedSession.instructorStudents.toLocaleString()} students
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="modal-section">
                <h4 className="modal-section-title">About this session</h4>
                <p className="modal-description">{selectedSession.description}</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LiveSessionsPage;


